// Privacy-respecting analytics. Plausible by default, gated on the
// `ct_cookie_consent` cookie (analytics=true). Re-checks consent on every
// call so revoking via /settings/privacy stops tracking immediately.
//
// Pageviews: see <AnalyticsListener/> mounted in __root.tsx, which subscribes
// to the TanStack router and fires trackPageview() on each resolved navigation.
//
// To swap in GA4, see the commented block at the bottom of this file.

import Plausible from "plausible-tracker";
import { hasConsent } from "@/lib/consent";

type PlausibleApi = ReturnType<typeof Plausible>;

const PLAUSIBLE_DOMAIN =
  (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined) ?? "currencythoughts.net";

let cached: PlausibleApi | null = null;

function getClient(): PlausibleApi | null {
  if (typeof window === "undefined") return null;
  if (!hasConsent("analytics")) return null;
  if (!cached) {
    cached = Plausible({
      domain: PLAUSIBLE_DOMAIN,
      apiHost: "https://plausible.io",
      trackLocalhost: false,
    });
  }
  return cached;
}

export function trackPageview(url?: string): void {
  const client = getClient();
  if (!client) return;
  try {
    client.trackPageview(url ? { url } : undefined);
  } catch {
    // Never let analytics break the app.
  }
}

export function trackEvent(name: string, props?: Record<string, string | number | boolean>): void {
  const client = getClient();
  if (!client) return;
  try {
    client.trackEvent(name, props ? { props } : undefined);
  } catch {
    // Swallow.
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Optional: GA4 alternative. Uncomment, `bun add react-ga4`, and replace
 * Plausible above if the team prefers Google Analytics.
 *
 *   import ReactGA from "react-ga4";
 *   const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
 *   let ga4Initialized = false;
 *   function ensureGa4() {
 *     if (typeof window === "undefined") return false;
 *     if (!hasConsent("analytics") || !GA4_ID) return false;
 *     if (!ga4Initialized) { ReactGA.initialize(GA4_ID); ga4Initialized = true; }
 *     return true;
 *   }
 *   export function trackPageview(url?: string) {
 *     if (!ensureGa4()) return;
 *     ReactGA.send({ hitType: "pageview", page: url ?? window.location.pathname });
 *   }
 *   export function trackEvent(name: string, props?: Record<string, unknown>) {
 *     if (!ensureGa4()) return;
 *     ReactGA.event(name, props as Record<string, string | number>);
 *   }
 * ────────────────────────────────────────────────────────────────────────── */